import { useMemo } from 'react';

import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Tooltip } from '@mui/material';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import usePricing from 'api/usePricing';
import useUsers from 'api/useUsers';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { EFeatureFlag } from 'constants/enums';
import dayjs from 'dayjs';
import { permissionsUtil } from 'utils/permissionsUtil';

import AcTooltip from 'components/AcTooltip/AcTooltip';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import EmptyState from 'components/EmptyState/EmptyState';
import { TableView } from 'components/TableView';

import './style.scss';

interface PricingTableRow {
  _id: string;
  usd: string;
  lastEdited: string;
  pricePointDeviations?: {
    usdValueMismatchCount?: number;
    conversionRateDeviationCount?: number;
  };
}

interface PricingOutletContext {
  isDeleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;
  selectedPricing: string | null;
  setSelectedPricing: (pricing: string | null) => void;
  onAddPricePoint: () => void;
}

const PricingTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { setDeleteDialogOpen, setSelectedPricing, onAddPricePoint } =
    useOutletContext<PricingOutletContext>();

  const { getPricings } = usePricing(publisherId);
  const { fetchFeatureFlags } = useUsers({ publisherId: publisherId });

  const isPricingManage = permissionsUtil.isActionEnabled(
    null,
    EPermissionGroup.PRICING,
    EPermissionAction.MANAGE
  );
  const isCanUserEdit = permissionsUtil.canUserEdit();
  const isDisabledDueToPermissions = useMemo(
    () => !isPricingManage || !isCanUserEdit,
    [isPricingManage, isCanUserEdit]
  );

  const hasFeatureFlagPricePointAlerting =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_PRICE_POINT_ALERTING
    ];

  const columns: ColumnDef<PricingTableRow>[] = useMemo(
    () => [
      {
        accessorKey: 'usd',
        header: 'USD Price Point',
        cell: (cell: CellContext<PricingTableRow, unknown>) => (
          <span className="text-black">{`$${cell.getValue()}`}</span>
        )
      },
      {
        accessorKey: 'lastEdited',
        header: 'Last Edited',
        cell: (cell: CellContext<PricingTableRow, unknown>) => (
          <span>
            {dayjs(String(cell.getValue())).utc().format(DATE_TIME_FORMAT)}
          </span>
        )
      },
      {
        accessorKey: 'pricePointDeviations',
        header: '',
        maxSize: 5,
        size: 5,
        cell: ({ row }: CellContext<PricingTableRow, void>) => {
          const {
            usdValueMismatchCount = 0,
            conversionRateDeviationCount = 0
          } = row.original.pricePointDeviations || {};

          const totalDeviations =
            usdValueMismatchCount + conversionRateDeviationCount;

          const shouldDisplayErrorIcon =
            totalDeviations > 0 && hasFeatureFlagPricePointAlerting;

          return (
            <div className="flex items-center justify-end gap-2 w-full">
              {shouldDisplayErrorIcon && (
                <Tooltip
                  arrow
                  title={
                    <AcTooltip
                      content={`Exchange Rate drift anomalies since last updated: ${totalDeviations}`}
                    />
                  }
                >
                  <ErrorOutlineOutlinedIcon sx={{ color: '#A8A9B2' }} />
                </Tooltip>
              )}
              <ActionCell
                className="justify-end"
                rowData={row.original}
                testId='PricingRowMoreButton'
                actions={[
                  {
                    label: 'Edit',
                    handler: (rowData: PricingTableRow) => {
                      navigate(`./form/${rowData._id}`);
                    },
                    disabled: isDisabledDueToPermissions
                  },
                  {
                    label: 'Delete',
                    handler: (rowData: PricingTableRow) => {
                      setDeleteDialogOpen(true);
                      setSelectedPricing(rowData._id);
                    },
                    disabled: isDisabledDueToPermissions
                  }
                ]}
              />
            </div>
          );
        }
      }
    ],
    [
      hasFeatureFlagPricePointAlerting,
      navigate,
      setDeleteDialogOpen,
      setSelectedPricing,
      isDisabledDueToPermissions
    ]
  );

  const tableData = useMemo(() => {
    if (!getPricings.data?.result) return [];
    return getPricings.data.result.map((pricing: any) => ({
      _id: pricing._id,
      usd: (pricing.priceInCents / 100).toFixed(2),
      lastEdited: pricing.updatedAt,
      pricePointDeviations: pricing?.pricePointDeviations
    }));
  }, [getPricings.data]);

  if (getPricings.data && getPricings.data.result.length === 0) {
    return (
      <EmptyState
        title="Add your first price point"
        description="Set up your pricing points to manage your store's pricing"
        buttonText="Add new price point"
        className="mx-auto mt-36"
        onClick={onAddPricePoint}
      />
    );
  }

  return (
    <div className="mt-5">
      <TableView
        rowIdIdentifier="_id"
        title="Price Points"
        data={tableData}
        columns={columns}
        isLoading={getPricings.isLoading}
        stickyHeader
        enableColumnResizing={false}
      />
    </div>
  );
};

export default PricingTable;
